import { generateQuestion, topicsForLevel, type LevelId, type Question, type Tier } from "@/math";
import type { AttemptRecord } from "@/state/store";

/** Short tamper-evident code printed on reports (FNV-1a over the records). */
export function verificationCode(records: AttemptRecord[], heroName: string): string {
  const text =
    heroName +
    "|" +
    records.map((r) => `${r.at}|${r.topicId}|${r.prompt}|${r.given}|${r.correct}`).join("\n");
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  const hex = hash.toString(16).toUpperCase().padStart(8, "0");
  return `${hex.slice(0, 4)}-${hex.slice(4)}`;
}

export interface TopicSummary {
  topicId: string;
  attempts: number;
  correct: number;
  avgTimeMs: number;
}

export function summarize(records: AttemptRecord[]): TopicSummary[] {
  const map = new Map<string, TopicSummary>();
  for (const r of records) {
    const s = map.get(r.topicId) ?? { topicId: r.topicId, attempts: 0, correct: 0, avgTimeMs: 0 };
    s.avgTimeMs = Math.round((s.avgTimeMs * s.attempts + r.timeMs) / (s.attempts + 1));
    s.attempts += 1;
    if (r.correct) s.correct += 1;
    map.set(r.topicId, s);
  }
  return [...map.values()].sort((a, b) => b.attempts - a.attempts);
}

/** Fixed-seed example questions per topic and tier, for the curriculum overview. */
export function curriculumSamples(
  level: LevelId,
  perTier = 3,
): Array<{ topicId: string; tier: Tier; questions: Question[] }> {
  const out: Array<{ topicId: string; tier: Tier; questions: Question[] }> = [];
  for (const topic of topicsForLevel(level)) {
    for (const tier of [1, 2, 3] as Tier[]) {
      const questions: Question[] = [];
      for (let i = 0; i < perTier; i++)
        questions.push(generateQuestion(topic.id, tier, 100000 + tier * 1000 + i * 7, "typed"));
      out.push({ topicId: topic.id, tier, questions });
    }
  }
  return out;
}
